import logError from 'src/data/services/logError';
import React, { useState, useEffect } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, Platform, Text } from 'react-native';
import { formatPageDay } from 'src/core/utils/formatTimetableTime';
import showAlert from 'src/core/utils/showAlert';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import checkIsEven from 'src/core/utils/checkIsEven';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Card from 'src/presentation/components/atoms/Card';
import Fonts from 'src/core/utils/Fonts';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Loader from 'src/presentation/components/atoms/Loader';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import EventDetailLink from 'src/presentation/components/molecules/EventDetailLink';
import Title from 'src/presentation/components/atoms/Title';
import Translations from 'src/core/utils/Translations';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import { navigatePage, replacePage } from 'src/presentation/helpers/NavigationHelper';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import isFavoritesPage from 'src/core/utils/isFavoritesPage';
import type Constants from 'src/core/utils/Constants';

function ProgramHolderScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenProgramHolder>>();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();

	const [refreshing, setRefreshing] = useState(false);
	const footerHeight = useFooterHeight();

	// The no-page state below is a loader, so this screen needs no separate `isLoading` branch.
	const { page } = usePageFromRoute(route?.params);
	const title = page?.name ?? '';

	const fetchUuid = navigationStore.activeEventUuid ?? eventStore.activeEventUuid;

	useEffect(() => {
		const init = async () => {
			if (!page) {
				await eventStore.fetchEvent(fetchUuid);
			}
		};
		init().catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// The programma's of a day: named, and each one once. A zaal without a name is no zaal.
	const stageTitles = (p: ApiPage) =>
		p.type === 'Program' ? [...new Set((p.data.stages ?? []).map((stage) => stage.title).filter(Boolean))] : [];

	// Mijn timetable and the days carrying a block schedule are overviews across the whole programme;
	// the other days are its plain lists.
	const isBlocksDay = (p: ApiPage) => stageTitles(p).length > 0;

	// One tile is no choice: whatever this screen would show is that one list, so it is a tap in the way and the
	// page behind it takes its place, with Terug still leading home. That holds for the day a participant came
	// for and for a programme that holds nothing but Mijn timetable (#430). Two programma's is a choice, and
	// then the tiles stay.
	const loneDay = ((): ApiPage | null => {
		const tiles = (page?.children ?? []).filter((child) => Number(child.hide_on_home) !== 1);
		const day = tiles[0];

		return tiles.length === 1 && day?.type === 'Program' && stageTitles(day).length < 2 ? day : null;
	})();

	useEffect(() => {
		if (loneDay) {
			replacePage(navigation, loneDay.type, { data: loneDay });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loneDay?.id]);

	const handleRefresh = () => {
		setRefreshing(true);
		eventStore
			.fetchEvent(fetchUuid, true)
			.then(() => {
				setRefreshing(false);
			})
			.catch(() => {
				setRefreshing(false);
			});
	};

	const space = (index: number) => ({ marginRight: checkIsEven(index) ? (Platform.OS === 'ios' ? 30 : 16) : 0 });

	const renderTiles = (tiles: ApiPage[]) =>
		tiles.map((tile, index) => (
			<EventDetailLink data={tile} key={tile.id} spaceAround={space(index)} theme={eventStore.activeTheme} />
		));

	/**
	 * The zalen of a day, each as a tile of its own: a participant picks the programma they follow, the way
	 * the old page-per-zaal did, while the beheerder keeps one day with its zalen.
	 */
	const renderStageTiles = (day: ApiPage, offset: number) => {
		if (day.type !== 'Program') return null;

		const titles = stageTitles(day);
		const colourOf = (title: string) =>
			(day.data.stages ?? []).find((stage) => stage.title === title)?.color || undefined;

		return titles.map((title, index) => (
			<EventDetailLink
				colour={colourOf(title)}
				data={day}
				key={`${day.id}-${title}`}
				label={title}
				spaceAround={space(offset + index)}
				theme={eventStore.activeTheme}
				onPress={() => {
					navigationStore.setNotificationOrigin('');
					navigatePage(navigation, day.type, day, { stage: title });
				}}
			/>
		));
	};

	const renderProgramDetailLinks = () => {
		if (!page) {
			if (eventStore.activeEventView?.length === 0) {
				showAlert({
					title: Translations.errorDefaultSubject,
					message: Translations.errorDefaultMessage,
					confirmText: Translations.buttonOke,
					confirmButtonStyle: 'default',
					onConfirm: () => {
						navigation.pop();
					},
				});
			}
			return null;
		}
		// The day a page belongs to; '' for a page that carries no date, which sorts last.
		const dateOf = (day: ApiPage) => (day.type === 'Program' ? (day.data.page_date ?? '') : '');

		// A tile a beheerder switched off is not in the app: that is how they fill a day, or hold a whole
		// programme back, before anyone sees it.
		const tiles = page.children.filter((child) => Number(child.hide_on_home) !== 1);

		if (tiles.length === 0) {
			return (
				<View style={styles.linkContainer}>
					<EmptyState
						icon="calendar-outline"
						message={Translations.textNoProgramMessage}
						title={Translations.textNoProgramTitle}
					/>
				</View>
			);
		}

		// A programme can leave the day tiles out: then a participant picks a programma of a day straight away,
		// and only Mijn timetable stays above them.
		const showDayTiles = page.type !== 'ProgramHolder' || page.data.program_view !== 'none';
		// A fixed order, the same one the panel shows: Mijn timetable leads, then the days in the order they
		// happen. A day without a date brings up the rear.
		const isMyTimetable = (page: ApiPage) => isFavoritesPage(page);
		// The parts tile of a day is the overview across its programma's. With one programma and a plain list
		// that overview IS that programma, so the two tiles opened the very same screen (#430). As a block
		// schedule it shows something else, and from two programma's on there is something to oversee.
		const partsView = page.type === 'ProgramHolder' ? (page.data.program_view ?? 'list') : 'list';
		const repeatsItsProgramme = (day: ApiPage) => stageTitles(day).length < 2 && partsView === 'list';
		const overview = tiles
			.filter((c) => isMyTimetable(c) || (showDayTiles && isBlocksDay(c) && !repeatsItsProgramme(c)))
			.sort((a, b) => {
				if (isMyTimetable(a) !== isMyTimetable(b)) {
					return isMyTimetable(a) ? -1 : 1;
				}

				return dateOf(a).localeCompare(dateOf(b)) * (dateOf(a) === '' || dateOf(b) === '' ? -1 : 1);
			});
		const listDays = tiles.filter((c) => !isFavoritesPage(c) && !isBlocksDay(c));
		const blocksDays = tiles.filter(isBlocksDay);

		// Nothing to group: an event without a blokkenschema keeps the flat grid it always had.
		if (blocksDays.length === 0) {
			return <View style={styles.linkContainer}>{renderTiles(tiles)}</View>;
		}

		// One section per day, in date order, holding the programma's of that day: the zalen of its
		// blokkenschema plus the days that are a programma of their own. '' collects what has no date.
		const dates = [...new Set([...blocksDays, ...listDays].map(dateOf))].sort((a, b) =>
			a === '' ? 1 : b === '' ? -1 : a.localeCompare(b),
		);

		// The day a beheerder named ("Dag 1", or whatever they called the day that carries the zalen) beats a
		// bare date; a date without such a day is numbered by its place in the programme.
		const dayName = (date: string) =>
			blocksDays.find((day) => dateOf(day) === date)?.name ?? Translations.titleDayNumber(dates.indexOf(date) + 1);

		// A single day needs no section of its own: its date belongs with the heading above the tiles.
		const onlyDate = dates.length === 1 ? dates[0] : undefined;

		return (
			<>
				<View style={styles.linkContainer}>{renderTiles(overview)}</View>
				<View style={styles.sectionTitle}>
					{/* The tiles above already carry their own bottom margin, so the title adds none of its own. */}
					<Title
						style={{ marginTop: 0 }}
						text={onlyDate ? `${Translations.titlePrograms} (${formatPageDay(onlyDate)})` : Translations.titlePrograms}
					/>
				</View>
				{dates.map((date) => {
					const daysOfDate = blocksDays.filter((day) => dateOf(day) === date);
					const listOfDate = listDays.filter((day) => dateOf(day) === date);
					let placed = 0;

					return (
						<View key={date === '' ? 'undated' : date}>
							{onlyDate === undefined && date !== '' ? (
								<Text
									style={[
										styles.dateHeader,
										// Only when the theme names a colour: an undefined one wipes the style's own.
										eventStore.activeTheme.heading_text_color ? { color: eventStore.activeTheme.heading_text_color } : null,
									]}
								>
									{`${dayName(date)} (${formatPageDay(date)})`}
								</Text>
							) : null}
							<View style={[styles.linkContainer, styles.sectionGrid]}>
								{daysOfDate.map((day) => {
									const tiles = renderStageTiles(day, placed);
									placed += tiles?.length ?? 0;

									return tiles;
								})}
								{listOfDate.map((day, index) => (
									<EventDetailLink data={day} key={day.id} spaceAround={space(placed + index)} theme={eventStore.activeTheme} />
								))}
							</View>
						</View>
					);
				})}
			</>
		);
	};

	return (
		<View renderToHardwareTextureAndroid={true} style={styles.layoutContainer}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>

			{!page ? (
				<Loader />
			) : (
				<Card backgroundColor={eventStore.activeTheme.app_bg_color} backgroundImage={eventStore.activeTheme.app_bg_image}>
					<ScrollView
						contentContainerStyle={{ flexGrow: 1, paddingBottom: footerHeight }}
						refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
						style={{ flex: 1 }}
					>
						<View style={styles.wrapContainer}>{renderProgramDetailLinks()}</View>
					</ScrollView>
				</Card>
			)}

			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default ProgramHolderScreen;

const styles = StyleSheet.create({
	wrapContainer: {
		// Stretch (not center) so the link grid fills the width and a single tile sits top-left at position 1
		// instead of being centered.
		alignItems: 'stretch',
	},
	linkContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		paddingTop: 28,
		paddingLeft: Dimens.padding40,
		paddingRight: Dimens.padding40,
	},
	sectionTitle: {
		// Title brings 20 of its own, so this lines the header up with the 40 the grid starts at.
		paddingLeft: Dimens.padding20,
		// The tiles above leave 30 (iOS) or 16 (Android) of their own; even that out to the 20 the
		// title leaves below itself.
		marginTop: Platform.OS === 'ios' ? -10 : 4,
	},
	sectionGrid: {
		// The section title already brings the air above this grid.
		paddingTop: 0,
	},
	dateHeader: {
		fontSize: Dimens.fontSize17,
		fontFamily: Fonts.montserratBold,
		color: Colors.greyTextDark,
		paddingLeft: Dimens.padding40,
		paddingBottom: Dimens.padding16,
	},
	layoutContainer: {
		flex: 1,
		backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
	},
});
